import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getAuthenticatedClient } from '@/services/client';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Leaf, 
  Bug, 
  CloudSun, 
  TrendingUp, 
  BookOpen, 
  Search, 
  Bookmark, 
  Clock,
  Play
} from 'lucide-react';
import { KnowledgeCategory, KnowledgeArticle, ReadingLevel } from './types';
import { cn } from '@/lib/utils';

interface KnowledgeBaseProps {
  onArticleSelect?: (article: KnowledgeArticle) => void;
  onAskAgent?: (question: string) => void;
}

export function KnowledgeBase({ onArticleSelect, onAskAgent }: KnowledgeBaseProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<KnowledgeCategory>('crops');
  const [readingLevel, setReadingLevel] = useState<ReadingLevel>('simple');
  const [bookmarked, setBookmarked] = useState<Set<string>>(new Set());
  const [recentlyViewed, setRecentlyViewed] = useState<string[]>([]);

  const { data: faqData = [] } = useQuery({
    queryKey: ['knowledge', 'faqs'],
    queryFn: async () => {
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('faq_questions')
        .select('id, question_text, answer_text, topic_category, language, is_active')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) return [];
      return data ?? [];
    },
    staleTime: 1000 * 60 * 30,
  });

  const articles: KnowledgeArticle[] = useMemo(() => {
    if (faqData.length > 0) {
      const categoryMap: Record<string, KnowledgeCategory> = {
        crops: 'crops',
        pests: 'pests',
        weather: 'weather',
        market: 'markets',
        livestock: 'crops',
        poultry: 'crops',
        health: 'pests',
        general: 'crops',
      };

      return faqData.map((faq: any) => ({
        id: faq.id,
        category: categoryMap[faq.topic_category?.toLowerCase()] ?? 'crops',
        title: faq.question_text,
        summary: faq.answer_text?.substring(0, 120) + ((faq.answer_text?.length ?? 0) > 120 ? '...' : ''),
        content: faq.answer_text ?? '',
        images: [],
        readingLevel: 'simple' as ReadingLevel,
        tags: [faq.topic_category?.toLowerCase() ?? 'general'],
        createdAt: new Date(),
        updatedAt: new Date(),
        isVideo: false,
        difficulty: 'beginner' as const,
      }));
    }

    return [];
  }, [faqData]);

  const categories = [
    { id: 'crops' as KnowledgeCategory, label: t('knowledge.tabs.crops'), icon: Leaf },
    { id: 'pests' as KnowledgeCategory, label: t('knowledge.tabs.pests'), icon: Bug },
    { id: 'weather' as KnowledgeCategory, label: t('knowledge.tabs.weather'), icon: CloudSun },
    { id: 'markets' as KnowledgeCategory, label: t('features.knowledge.markets'), icon: TrendingUp },
    { id: 'best_practices' as KnowledgeCategory, label: t('features.knowledge.bestPractices'), icon: BookOpen },
  ];

  const filteredArticles = useMemo(() => {
    return articles.filter(article => {
      const matchesCategory = article.category === selectedCategory;
      const matchesSearch = searchQuery === '' || 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesLevel = article.readingLevel === readingLevel;
      
      return matchesCategory && matchesSearch && matchesLevel;
    });
  }, [articles, selectedCategory, searchQuery, readingLevel]);

  const handleArticleClick = (article: KnowledgeArticle) => {
    setRecentlyViewed(prev => [article.id, ...prev.filter(id => id !== article.id)].slice(0, 10));
    onArticleSelect?.(article);
  };

  const toggleBookmark = (articleId: string) => {
    setBookmarked(prev => {
      const newSet = new Set(prev);
      if (newSet.has(articleId)) {
        newSet.delete(articleId);
      } else {
        newSet.add(articleId);
      }
      return newSet;
    });
  };

  const getDifficultyIndicator = (difficulty?: string) => {
    switch (difficulty) {
      case 'beginner':
        return '🌱';
      case 'intermediate':
        return '🌿🌿';
      case 'advanced':
        return '🌳🌳🌳';
      default:
        return '🌱';
    }
  };

  const getCategoryColor = (category: KnowledgeCategory) => {
    const colors = {
      crops: 'from-green-500 to-green-600',
      pests: 'from-red-500 to-red-600',
      weather: 'from-blue-500 to-blue-600',
      markets: 'from-purple-500 to-purple-600',
      best_practices: 'from-orange-500 to-orange-600',
    };
    return colors[category];
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Search Bar */}
      <div className="p-4 border-b border-border">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('features.knowledge.searchKnowledgeBase')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {/* Reading Level Toggle */}
        <div className="flex gap-2 mt-3">
          <Button
            variant={readingLevel === 'simple' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setReadingLevel('simple')}
          >
            {t('features.knowledge.simple')}
          </Button>
          <Button
            variant={readingLevel === 'detailed' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setReadingLevel('detailed')}
          >
            {t('features.knowledge.detailed')}
          </Button>
        </div>
      </div>

      <Tabs value={selectedCategory} onValueChange={(v) => setSelectedCategory(v as KnowledgeCategory)} className="flex-1 flex flex-col">
        {/* Category Tabs */}
        <TabsList className="w-full justify-start overflow-x-auto flex-shrink-0 rounded-none border-b">
          {categories.map(cat => (
            <TabsTrigger key={cat.id} value={cat.id} className="gap-2">
              <cat.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{cat.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Content Area */}
        {categories.map(cat => (
          <TabsContent key={cat.id} value={cat.id} className="flex-1 mt-0">
            <ScrollArea className="h-full">
              <div className="p-4">
                {filteredArticles.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>{t('features.knowledge.noArticlesFound')}</p>
                    <Button
                      variant="link"
                      onClick={() => onAskAgent?.(`Tell me about ${selectedCategory}`)}
                      className="mt-2"
                    >
                      {t('agent.askInstead')}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {filteredArticles.map(article => (
                      <Card
                        key={article.id}
                        className="relative overflow-hidden cursor-pointer active:scale-[0.98] transition-transform rounded-xl h-[180px] flex flex-col"
                        onClick={() => handleArticleClick(article)}
                      >
                        {/* Thumbnail - 70% */}
                        <div className="relative h-[70%] overflow-hidden">
                          {article.images[0] ? (
                            <img
                              src={article.images[0]}
                              alt={article.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            // Placeholder with category color
                            <div className={cn(
                              "w-full h-full bg-gradient-to-br flex items-center justify-center",
                              getCategoryColor(article.category)
                            )}>
                              <cat.icon className="h-12 w-12 text-white/80" />
                            </div>
                          )}
                          
                          {/* Video Play Button Overlay */}
                          {(article as any).isVideo && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                              <div className="bg-white/90 rounded-full p-3">
                                <Play className="h-6 w-6 text-gray-900 fill-gray-900" />
                              </div>
                            </div>
                          )}

                          {/* Difficulty Indicator */}
                          <div className="absolute top-2 right-2 text-lg bg-white/90 rounded-full px-2 py-1">
                            {getDifficultyIndicator((article as any).difficulty)}
                          </div>

                          {/* Bookmark Button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute top-2 left-2 h-8 w-8 bg-white/90 hover:bg-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleBookmark(article.id);
                            }}
                          >
                            <Bookmark
                              className={cn(
                                "h-4 w-4",
                                bookmarked.has(article.id) && "fill-current text-yellow-500"
                              )}
                            />
                          </Button>
                        </div>

                        {/* Title Bar - 30% */}
                        <div className="h-[30%] flex items-center px-3 bg-white/95 backdrop-blur-sm">
                          <h3 className="font-bold text-sm leading-tight line-clamp-2">
                            {article.title}
                          </h3>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        ))}
      </Tabs>

      {/* Recently Viewed */}
      {recentlyViewed.length > 0 && (
        <div className="border-t border-border p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{t('features.knowledge.recentlyViewed')}</span>
          </div>
          <div className="flex gap-2 overflow-x-auto">
            {recentlyViewed.slice(0, 5).map(id => {
              const article = articles.find(a => a.id === id);
              return article ? (
                <Button
                  key={id}
                  variant="outline"
                  size="sm"
                  onClick={() => handleArticleClick(article)}
                  className="flex-shrink-0"
                >
                  {article.title}
                </Button>
              ) : null;
            })}
          </div>
        </div>
      )}
    </div>
  );
}
